import { useMemo, useCallback, useEffect } from 'react';
import { capitalize } from 'lodash';
import { analyticsClient, EventType, Properties } from 'src/shared/services/analytics';
import { EligibilityStatus, InviteDependentStepNames } from './pages/InviteDependentPage';

/** FYI: Any view (aka steps -> InviteDependentStepNames) in invite-dependent is a page
 * (despite all being on one route path /invite-dependent) */
export enum DependentsPageSlug {
    Welcome = 'invite-dependents-home',
    EligibleForm = 'check-eligibility-form',
    Loading = 'loading',
    NotEligible = 'no-eligibility',
    EligibleToTherapy = 'eligible-for-therapy',
    EligibleToJoin = 'eligible-to-join',
    JoinForm = 'invite-form',
    TherapyForm = 'request-therapy-form',
    RequestSent = 'request-sent',
    InviteSent = 'invite-sent',
}

export const slugToNormalText = (slug: DependentsPageSlug): string => {
    return capitalize(slug).replace(/-/g, ' ');
};

const dependentsStateToPageSlugMap: Record<
    InviteDependentStepNames | 'inviteSent',
    DependentsPageSlug
> = {
    welcome: DependentsPageSlug.Welcome,
    eligibleForm: DependentsPageSlug.EligibleForm,
    loading: DependentsPageSlug.Loading,
    notEligible: DependentsPageSlug.NotEligible,
    eligibleToTherapy: DependentsPageSlug.EligibleToTherapy,
    eligibleToJoin: DependentsPageSlug.EligibleToJoin,
    joinForm: DependentsPageSlug.JoinForm,
    therapyForm: DependentsPageSlug.TherapyForm,
    requestSent: DependentsPageSlug.RequestSent,
    inviteSent: DependentsPageSlug.InviteSent,
};

export const useInviteDependentsAnalytics = (
    stepName: InviteDependentStepNames,
    trackPageView = false,
    eligibilityStatus?: EligibilityStatus,
) => {
    const pageSlug = useMemo(() => {
        const shouldShowInviteSent = stepName === 'requestSent' && eligibilityStatus === 'join';
        if (shouldShowInviteSent) {
            return dependentsStateToPageSlugMap.inviteSent;
        }
        return dependentsStateToPageSlugMap[stepName];
    }, [stepName, eligibilityStatus]);

    const trackClick = useCallback(
        (actionName: string, properties?: Properties) => {
            analyticsClient.trackClick(`Invite Dependent: ${actionName}`, {
                ...properties,
                page_name: pageSlug,
            });
        },
        [pageSlug],
    );

    useEffect(() => {
        if (trackPageView) {
            const viewPageName = slugToNormalText(pageSlug);

            analyticsClient.track(viewPageName, EventType.PAGEVIEW, {
                page_name: pageSlug,
            });
        }
    }, [trackPageView, pageSlug]);

    return { trackClick };
};
